/**
 * Signing from a link.
 *
 * This is the only screen in the app a stranger can reach, so it is built on
 * the assumption that whoever opened it should not be trusted until they prove
 * otherwise. It never touches the database directly: every call goes to the
 * worker carrying both the link token and the code the dealership read out
 * over the phone, and the worker re-checks both every time. Nothing is kept in
 * the browser that would let a second person pick up the device and sign.
 *
 * It is also the weaker of the two signing routes, and says so. A code read
 * over the telephone proves the person holds a number the dealership already
 * had; it does not prove who they are the way looking at their licence does.
 * The certificate records which was used.
 */

import { useState } from "react";
import { Button, ErrorNote } from "../../components/ui";
import SignaturePad from "../../components/SignaturePad";
import PdfPages from "../../components/PdfPages";
import { LogoMark } from "../../components/ui";
import { ESIGN_DISCLOSURE } from "../../forms/signatureCertificate";
import { openSigning, submitSignature, type RemoteSession } from "../../lib/signing/remote";

type Step = "code" | "review" | "consent" | "sign" | "done";

export default function RemoteSign({ token }: { token: string }) {
  const [step, setStep] = useState<Step>("code");
  const [code, setCode] = useState("");
  const [session, setSession] = useState<RemoteSession | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [role, setRole] = useState<"buyer" | "co_buyer">("buyer");
  /* Whether the packet is actually on the screen. Nobody gets past this step
     until it is: a signer who cannot see the paperwork must not be able to
     sign it, and before this the continue button sat there live under an
     empty box. Page count comes from the document rather than doc_keys,
     because a packet is not one page per document and somebody deciding
     whether they have reached the end needs the real number. */
  const [doc, setDoc] = useState<
    { kind: "loading" } | { kind: "ready"; pages: number } | { kind: "failed" }
  >({ kind: "loading" });
  const [drawn, setDrawn] = useState<string | null>(null);
  const [initials, setInitials] = useState<string | null>(null);

  const unlock = async () => {
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) { setErr("The code is six digits."); return; }
    setBusy(true); setErr(null);
    try {
      const s = await openSigning(token, digits);
      setSession(s);
      // If the co-buyer has not signed but the buyer has, offer the co-buyer.
      if (s.signedRoles.includes("buyer") && s.coBuyerName
          && !s.signedRoles.includes("co_buyer")) {
        setRole("co_buyer");
      }
      setStep("review");
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const sign = async () => {
    if (!drawn || !session) return;
    setBusy(true); setErr(null);
    try {
      await submitSignature(token, code.replace(/\D/g, ""), {
        role, image: drawn, initials, withConsent: !session.consentGiven,
      });
      setStep("done");
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const whoName = role === "buyer" ? session?.buyerName : session?.coBuyerName;

  return (
    <div className={`signshell${step === "review" ? " signshell-doc" : ""}`}>
      <header className="signhead">
        <LogoMark size={30} />
        <span>Cape Auto Solutions</span>
      </header>

      <main className="signmain">
        {err && <ErrorNote>{err}</ErrorNote>}

        {step === "code" && (
          <>
            <h1>Sign your paperwork</h1>
            <p className="lede">
              The dealership will read you a six-digit code over the phone.
              Enter it here to open your documents.
            </p>
            <div className="field">
              <label htmlFor="code">Six-digit code</label>
              <input
                id="code"
                className="codebox mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </div>
            <Button disabled={busy || code.replace(/\D/g, "").length !== 6} onClick={unlock}>
              {busy ? "Checking…" : "Open my documents"}
            </Button>
            <p className="finehint">
              If you do not have a code, call the dealership. Do not share this
              link or your code with anyone else. If you have been here before
              and are starting again, that is fine — the same code still works
              and nothing you did earlier was lost.
            </p>
          </>
        )}

        {step === "review" && session && (
          <>
            {/* Compact on purpose. The four-row card belongs on a page whose
                subject is the deal; here the subject is the document, and every
                row the card takes is a row of paperwork the customer cannot
                see. On a 360-wide phone the full card pushed the document off
                the screen entirely. */}
            <div className="reviewtop">
              <h1>Read before you sign</h1>
              <div className="docsummary">
                <strong>{session.vehicle}</strong>
                <span className="mono">{session.vin}</span>
                <span>Deal {session.dealNo} · {session.docCount} documents · take as long as you need</span>
              </div>
            </div>
            {session.packetUrl ? (
              <>
                {/* Drawn into this page, not handed to the browser's PDF
                    viewer. An iframe does not work on a phone — Android
                    Chrome refuses to render a PDF in one and offers a
                    download, iOS Safari shows page one and will not scroll —
                    so there was a link out beside it, and that link was the
                    trap: it took the signer to a bare PDF with no way back to
                    the button they were meant to press next. There is now
                    nowhere to go. The pages scroll here and the button below
                    stays put. */}
                <div className="packetframe">
                  <PdfPages url={session.packetUrl} onState={setDoc} />
                </div>

                <p className="finehint">
                  {doc.kind === "ready"
                    ? `Scroll to the end — ${doc.pages} page${doc.pages === 1 ? "" : "s"}. `
                       + "You will be emailed a signed copy to keep."
                    : doc.kind === "failed"
                      ? "Call the dealership on this one — nothing has been signed."
                      : "Loading your paperwork."}
                </p>

                <div className="actions stack">
                  <Button disabled={doc.kind !== "ready"}
                          onClick={() => setStep(session.consentGiven ? "sign" : "consent")}>
                    {doc.kind === "failed"
                      ? "Paperwork unavailable"
                      : doc.kind === "loading"
                        ? "Loading your paperwork"
                        : "I have read it — continue"}
                  </Button>
                </div>
              </>
            ) : (
              <ErrorNote>
                The documents could not be loaded. Please call the dealership
                before signing anything.
              </ErrorNote>
            )}
          </>
        )}

        {step === "consent" && (
          <>
            <h1>Signing electronically</h1>
            <div className="disclosure">{ESIGN_DISCLOSURE}</div>
            <Button disabled={busy} onClick={() => setStep("sign")}>
              I agree — continue
            </Button>
            <p className="finehint">
              If you would rather sign on paper, close this page and call the
              dealership. Nothing has been signed yet.
            </p>
          </>
        )}

        {step === "sign" && session && (
          <>
            <h1>Your signature</h1>
            {session.coBuyerName && (
              <div className="whoswitch">
                <button className={role === "buyer" ? "on" : ""}
                        disabled={session.signedRoles.includes("buyer")}
                        onClick={() => setRole("buyer")}>
                  {session.buyerName}
                  {session.signedRoles.includes("buyer") && " ✓"}
                </button>
                <button className={role === "co_buyer" ? "on" : ""}
                        disabled={session.signedRoles.includes("co_buyer")}
                        onClick={() => setRole("co_buyer")}>
                  {session.coBuyerName}
                  {session.signedRoles.includes("co_buyer") && " ✓"}
                </button>
              </div>
            )}
            <p className="lede">
              Signing as <strong>{whoName}</strong>. Use your finger or a stylus.
            </p>
            <SignaturePad label="Signature" onChange={setDrawn} height={200} />
            <SignaturePad label="Initials" onChange={setInitials} height={140} />
            <Button disabled={busy || !drawn} onClick={sign}>
              {busy ? "Sending…" : "Sign the paperwork"}
            </Button>
          </>
        )}

        {step === "done" && (
          <div className="signdone">
            <h1>Signed</h1>
            <p className="lede">
              Thank you{whoName ? `, ${whoName.split(" ")[0]}` : ""}. Your
              signature is recorded and the dealership has been told.
            </p>
            <p className="lede">
              A confirmation is on its way to your email now. Once the
              dealership has countersigned, they will send you the complete
              signed copy — keep that one.
            </p>
            <p className="finehint">You can close this page.</p>
          </div>
        )}
      </main>
    </div>
  );
}
